/**
 * zip 包要求区 — 固定三列：包内组成 | 命名要求 | 打包示例
 *
 * 命名列：有 optional 时分区展示；必传/非必传靠样式区分，字样只在标题旁图例。
 */

import { resolveRequiredPatterns } from '../genericResourceZipPackageSpec'

/** 长列表阈值：中列「必须」段内改用双列网格（无 optional 分区时） */
export const ZIP_EXACT_DENSE_THRESHOLD = 4

/** @typedef {'bullets' | 'columns-2' | 'req-opt-split' | 'example'} ZipListMode */
/** @typedef {{ text: string, required: boolean | null }} ZipPackageItem */

/** @type {Record<'includes' | 'required' | 'example', string>} */
export const ZIP_LAYOUT_COLUMN_TITLES = {
  includes: '包内组成',
  required: '命名要求',
  example: '打包示例',
}

/**
 * @param {string} text
 * @param {Set<string>} optionalSet
 * @param {boolean} markRequiredOptional
 * @returns {ZipPackageItem}
 */
function toZipPackageItem(text, optionalSet, markRequiredOptional) {
  if (!markRequiredOptional) {
    return { text, required: null }
  }
  return { text, required: !optionalSet.has(text) }
}

/** 与 Registry slot 对齐：legacy 类型未导出 includes 时的前端兜底 */
const ZIP_INCLUDES_FALLBACK = {
  Filter: ['滤镜主图', '预览图'],
  Clothing: ['服装主图', '预览图'],
  Template: ['预览图'],
  Flow: ['预览图 preview0', '预览图 preview1', '配置文件'],
  'recipe(食谱)': ['预览图', '封面图', '食谱正文'],
  'puzzle(拼图)': ['拼图主图', '预览图'],
  'level(关卡)': ['关卡图片 image1', '关卡图片 image2', '配置文件'],
  recipe_v2: ['菜品预览图', '食谱 JSON 配置'],
}

/**
 * @param {{ includes?: string[] } | null | undefined} spec
 * @param {string} assetType
 * @returns {ZipPackageItem[]}
 */
function resolveIncludeItems(spec, assetType) {
  const fromSpec = (spec && spec.includes) || []
  const texts = fromSpec.length ? fromSpec : (ZIP_INCLUDES_FALLBACK[assetType] || [])
  return texts.map((text) => ({ text, required: null }))
}

/**
 * 命名要求列展示用的 pattern 列表（含可选项，完整展示）。
 */
function resolveNamingDisplayPatterns(spec) {
  const pattern = (spec && spec.pattern) || []
  const exact = (spec && spec.exact) || []
  if (pattern.length || exact.length) {
    return [...pattern, ...exact]
  }
  return resolveRequiredPatterns(spec)
}

/**
 * @param {{ includes?: string[], exact?: string[], pattern?: string[], optional?: string[], examples?: string[] } | null | undefined} spec
 * @param {string} [assetType]
 * @returns {Array<{
 *   key: 'includes' | 'required' | 'example',
 *   title: string,
 *   items: ZipPackageItem[],
 *   listMode: ZipListMode,
 *   showNamingLegend?: boolean,
 *   requiredColumn?: ZipPackageItem[],
 *   optionalColumn?: ZipPackageItem[],
 * }>}
 */
export function buildZipPackageSections(spec, assetType = '') {
  const examples = (spec && spec.examples) || []
  const namingPatterns = resolveNamingDisplayPatterns(spec)
  const optionalSet = new Set((spec && spec.optional) || [])
  const markRequiredOptional = optionalSet.size > 0
  const alternativePatterns = spec && spec.anyOf
    ? namingPatterns.filter((text) => !optionalSet.has(text))
    : []
  const requiredItems = spec && spec.anyOf
    ? [
      ...(alternativePatterns.length
        ? [{ text: alternativePatterns.join(' 或 '), required: true }]
        : []),
      ...namingPatterns
        .filter((text) => optionalSet.has(text))
        .map((text) => ({ text, required: false })),
    ]
    : namingPatterns.map((text) => toZipPackageItem(text, optionalSet, markRequiredOptional))
  const requiredColumn = requiredItems.filter((item) => item.required === true)
  const optionalColumn = requiredItems.filter((item) => item.required === false)
  // 任一侧有「有标记的」命名项就走分区；空侧由 UI 单列收拢，避免空白半栏
  const useReqOptSplit =
    markRequiredOptional && (requiredColumn.length > 0 || optionalColumn.length > 0)

  return [
    {
      key: 'includes',
      title: ZIP_LAYOUT_COLUMN_TITLES.includes,
      items: resolveIncludeItems(spec, assetType),
      listMode: 'bullets',
    },
    {
      key: 'required',
      title: ZIP_LAYOUT_COLUMN_TITLES.required,
      items: requiredItems,
      listMode: useReqOptSplit
        ? 'req-opt-split'
        : namingPatterns.length >= ZIP_EXACT_DENSE_THRESHOLD
          ? 'columns-2'
          : 'bullets',
      showNamingLegend: useReqOptSplit,
      requiredColumn: useReqOptSplit ? requiredColumn : [],
      optionalColumn: useReqOptSplit ? optionalColumn : [],
    },
    {
      key: 'example',
      title: ZIP_LAYOUT_COLUMN_TITLES.example,
      items: examples.map((text) => ({ text, required: null })),
      listMode: 'example',
    },
  ]
}

/** 固定三列布局，不再按段数动态伸缩 */
export function zipPackageColumnCount() {
  return 3
}
